/**
 * V7.1 NPC character sheets (2026-06-02) — npc_id → NpcSheetData resolver.
 *
 * The SPA stores NPC data in two places inside the scenario JSON:
 *
 *   1. scenario.key_npcs[]    — named scenario NPCs (Pamela Holck,
 *                               Ahmal Ben Gazi, Dr. Julius No, …).
 *                               id = entry.id ('pamela_holck' etc).
 *                               sketch = entry.biographical_note.
 *
 *   2. scenario.guards_archetype — the top-level mook record. There is
 *                                  no `id` field in the raw data, so the
 *                                  SPA assigns the synthetic id
 *                                  'guards_archetype'.
 *                                  sketch = description_verbatim.
 *
 * This hook normalizes either source into NpcSheetData so NpcCard +
 * NpcDrawer don't branch on data shape. Mook records carry isMook=true
 * so the card can render the Combat / Mooks visual variant.
 *
 * Returns null for unknown ids — callers (NpcCard) gracefully render a
 * placeholder so a stale npc_inline_refs entry doesn't crash the UI.
 */
import { useMemo } from 'react';
import type {
  AdventureScenario,
  GuardsArchetypeRaw,
  KeyNpcRaw,
  NpcSheetData,
} from '../types/scenario';
import { allPersonnelIds, lookupPersonnel } from '../services/personnelCatalog';

export const GUARDS_ARCHETYPE_ID = 'guards_archetype';

function fromKeyNpc(raw: KeyNpcRaw): NpcSheetData {
  return {
    id: raw.id,
    name: raw.name,
    role: raw.role,
    sketch: raw.biographical_note ?? raw.role,
    stats: raw.stats ?? {},
    skills: raw.skills ?? {},
    physical: raw.physical ?? {},
    combat: raw.combat ?? {},
    isMook: false,
    page: raw.page,
    allegiance: raw.allegiance,
  };
}

function fromGuardsArchetype(raw: GuardsArchetypeRaw): NpcSheetData {
  return {
    id: GUARDS_ARCHETYPE_ID,
    name: raw.title,
    role: 'Mook archetype',
    sketch: raw.description_verbatim,
    stats: raw.stats ?? {},
    skills: raw.skills ?? {},
    physical: raw.physical ?? {},
    combat: {},
    isMook: true,
    page: raw.page,
  };
}

export function lookupNpc(
  scenario: AdventureScenario | null | undefined,
  npcId: string,
): NpcSheetData | null {
  if (npcId.length === 0) {
    return null;
  }
  if (scenario && npcId === GUARDS_ARCHETYPE_ID) {
    const ga = scenario.guards_archetype;
    return ga ? fromGuardsArchetype(ga) : null;
  }
  // Scenario-specific wins over personnel/ when both exist (lets a
  // scenario override an NPC's stats for that adventure).
  if (scenario) {
    const match = (scenario.key_npcs ?? []).find((n) => n.id === npcId);
    if (match) return fromKeyNpc(match);
  }
  // V7.2.C (2026-06-02): fall back to the bundled personnel catalog.
  return lookupPersonnel(npcId);
}

export function listAllNpcs(
  scenario: AdventureScenario | null | undefined,
): NpcSheetData[] {
  const out: NpcSheetData[] = [];
  const seenIds = new Set<string>();
  for (const raw of scenario?.key_npcs ?? []) {
    out.push(fromKeyNpc(raw));
    seenIds.add(raw.id);
  }
  if (scenario?.guards_archetype) {
    out.push(fromGuardsArchetype(scenario.guards_archetype));
    seenIds.add(GUARDS_ARCHETYPE_ID);
  }
  // V7.2.C: extend with the bundled personnel catalog (scenario.key_npcs
  // already overrode anything ambiguous via the seenIds dedup).
  for (const npcId of allPersonnelIds()) {
    if (seenIds.has(npcId)) continue;
    const personnel = lookupPersonnel(npcId);
    if (personnel !== null) out.push(personnel);
  }
  return out;
}

export interface UseNpcLookupResult {
  /** Resolve one id to its normalized sheet, or null if missing. */
  lookup(npcId: string): NpcSheetData | null;
  /** Resolve a list of ids, dropping unknowns. Stable reference per ids list value. */
  lookupMany(npcIds: readonly string[]): NpcSheetData[];
  /** Every NPC in the scenario (named + mooks), in stable order. */
  all: NpcSheetData[];
}

/**
 * Memoized scenario → NPC lookup. The hook re-derives only when the
 * scenario reference changes (i.e. on scenario JSON swap), so it's cheap
 * for the lifetime of a single campaign.
 */
export function useNpcLookup(
  scenario: AdventureScenario | null | undefined,
): UseNpcLookupResult {
  return useMemo(() => {
    const all = listAllNpcs(scenario);
    const lookup = (npcId: string) => lookupNpc(scenario, npcId);
    const lookupMany = (npcIds: readonly string[]) =>
      npcIds
        .map((id) => lookup(id))
        .filter((n): n is NpcSheetData => n !== null);
    return { lookup, lookupMany, all };
  }, [scenario]);
}
